import React from "react";
import { Play, Download, FileText, Music, Video, Image as ImageIcon, Star, Package, Lock, ExternalLink } from "lucide-react";
import { downloadFile } from "../../lib/downloadFile";

const CATEGORY_ICON = {
  "sample-packs": Music,
  content: Video,
  lessons: Play,
  resources: FileText,
};

function pickPrimaryAttachment(attachments = []) {
  if (!attachments || attachments.length === 0) return null;
  // Prefer video > audio > file > image (image-only perks render with no primary CTA).
  const byKind = (k) => attachments.find((a) => a.kind === k);
  return byKind("video") || byKind("audio") || byKind("file") || byKind("image") || attachments[0];
}

function FallbackThumb({ name, slug }) {
  const Icon = CATEGORY_ICON[slug] || Package;
  return (
    <div className="aspect-[16/10] w-full bg-gradient-to-br from-[#161a2c] via-[#10162a] to-[#0b0f1e] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 [background:radial-gradient(circle_at_30%_30%,#f5c14b,transparent)]" />
      <Icon className="w-12 h-12 text-paul-yellow/40" strokeWidth={1.5} />
      <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold text-white/40">
        {name}
      </span>
    </div>
  );
}

export default function PerkCard({ perk, category, onOpen, locked = false }) {
  const primary = pickPrimaryAttachment(perk.attachments);
  const hasExternal = Boolean(perk.externalUrl);
  const canWatch =
    primary && (primary.kind === "video" || primary.kind === "audio");
  const canDownload = primary && (primary.kind === "file" || primary.kind === "image");

  // When an external link is set, the card always opens the modal first
  // (lets the user read the description + see the thumbnail before they
  // leave the site). Files and link can coexist — the modal shows both.
  const handlePrimary = () => {
    if (locked) return;
    if (hasExternal) {
      onOpen?.(perk);
    } else if (canWatch) {
      onOpen?.(perk);
    } else if (canDownload && primary?.url) {
      downloadFile(primary.url, primary.name || perk.title || "perk-file");
    }
  };

  return (
    <article
      data-testid={`perk-card-${perk.id}`}
      className="group rounded-2xl bg-[#0f1322] border border-white/5 overflow-hidden hover:border-paul-yellow/30 transition-all hover:shadow-[0_10px_30px_rgba(245,193,75,0.08)] flex flex-col"
    >
      {/* Thumbnail */}
      <button
        type="button"
        onClick={handlePrimary}
        className="relative block text-left"
        aria-label={`Open ${perk.title}`}
        data-testid={`perk-card-thumb-${perk.id}`}
      >
        {perk.thumbnailUrl ? (
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#0b0f1e]">
            <img
              src={perk.thumbnailUrl}
              alt={perk.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {canWatch && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-14 h-14 rounded-full bg-paul-yellow text-navy flex items-center justify-center">
                  <Play className="w-6 h-6 fill-current ml-0.5" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <FallbackThumb name={category?.name || ""} slug={category?.slug || ""} />
        )}
        {perk.featured && (
          <span
            data-testid={`perk-card-featured-${perk.id}`}
            className="absolute top-3 left-3 inline-flex items-center gap-1 bg-paul-yellow text-navy text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded"
          >
            <Star className="w-3 h-3 fill-current" /> Featured
          </span>
        )}
      </button>

      {/* Body */}
      <div className="p-4 md:p-5 flex flex-col flex-1">
        {category && (
          <div
            data-testid={`perk-card-cat-${perk.id}`}
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-paul-yellow mb-2"
          >
            {category.name}
          </div>
        )}
        <h3
          data-testid={`perk-card-title-${perk.id}`}
          className="text-base md:text-lg font-extrabold text-white leading-tight mb-1.5 line-clamp-2"
        >
          {perk.title}
        </h3>
        {perk.description && (
          <p className="text-sm text-white/60 leading-relaxed line-clamp-3 mb-4">
            {perk.description}
          </p>
        )}
        <div className="mt-auto">
          {locked ? (
            <a
              href="/studio/profile"
              data-testid={`perk-card-locked-${perk.id}`}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white/70 hover:text-paul-yellow hover:border-paul-yellow/40 font-bold text-sm px-4 py-2 rounded-lg transition-colors"
            >
              <Lock className="w-3.5 h-3.5" />
              Reactivate to access
            </a>
          ) : hasExternal ? (
            <button
              type="button"
              data-testid={`perk-card-open-${perk.id}`}
              onClick={handlePrimary}
              className="inline-flex items-center gap-2 bg-paul-yellow hover:brightness-105 text-navy font-bold text-sm px-4 py-2 rounded-lg transition-all"
              style={{ color: "#0b1535" }}
            >
              <ExternalLink className="w-4 h-4" />
              Open
            </button>
          ) : canWatch ? (
            <button
              type="button"
              data-testid={`perk-card-watch-${perk.id}`}
              onClick={handlePrimary}
              className="inline-flex items-center gap-2 bg-paul-yellow hover:brightness-105 text-navy font-bold text-sm px-4 py-2 rounded-lg transition-all"
              style={{ color: "#0b1535" }}
            >
              <Play className="w-4 h-4 fill-current" />
              {primary.kind === "video" ? "Watch" : "Listen"}
            </button>
          ) : canDownload ? (
            <button
              type="button"
              data-testid={`perk-card-download-${perk.id}`}
              onClick={handlePrimary}
              className="inline-flex items-center gap-2 bg-paul-yellow hover:brightness-105 text-navy font-bold text-sm px-4 py-2 rounded-lg transition-all"
              style={{ color: "#0b1535" }}
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          ) : (
            <span className="text-xs text-white/30">No download attached</span>
          )}
        </div>
      </div>
    </article>
  );
}
